import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'

import {
  Exclamation,
  IconButton,
  ResponsiveImage,
  ScreenReaderOnly,
  SelectMark,
  TextLink,
  TypeStyleBodySmall,
  TypeStyleHeadlineExtraExtraSmall,
  timingDefault,
  useIntersection,
} from '@syconium/little-miss-figgy'
import { fromMd } from '@syconium/little-miss-figgy'

import { NavContainer } from '../../../brunswick/containers/NavContainer'
import { promoBarHeightDesktop, promoBarHeightMobile } from '../../../constants'
import { useNavHeader } from '../../_providers/NavHeaderProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { NextLink } from '../navigation/NextLink'

import { NextDialog, NextDialogProvider, useNextDialogContext } from './NextDialog.client'

type ToastNotificationEvent =
  | FavoritesAddToastNotificationEvent
  | FavoritesErrorToastNotificationEvent

type FavoritesAddToastNotificationEvent = {
  type: 'favorites-add-toast-notification'
  payload: { productHandle: string; toastImage: string }
}

type FavoritesErrorToastNotificationEvent = {
  type: 'favorites-error-toast-notification'
  payload: { productHandle: string; toastImage: string }
}

export const FavoritesErrorToastNotification: React.FC<{
  toastImage: string
  productHandle: string
  removeToastNotification: () => void
}> = ({ toastImage, productHandle, removeToastNotification }) => {
  const translations = useTranslation()
  return (
    <ToastNotification onAnimationEnd={() => removeToastNotification()}>
      <ToastImage
        src={toastImage}
        srcs={{ sm: toastImage, md: toastImage }}
        aspectRatios={null}
        alt={productHandle}
        widths={{ unit: 'px', sm: 368, md: 368 }}
        loading={'default'}
      ></ToastImage>
      <ToastRight>
        <ToastDescription>
          <ErrorHeadline role='alert' aria-live='polite'>
            <Exclamation />
            {translations.favorites.toastErrorAdding}
          </ErrorHeadline>
          <TypeStyleBodySmall>{translations.favorites.toastTryAgain}</TypeStyleBodySmall>
        </ToastDescription>
      </ToastRight>
      <StyledIconButton
        variant='black-on-white-no-border'
        onClick={() => removeToastNotification()}
      >
        <ScreenReaderOnly>{translations.magnolia.general.closeToast}</ScreenReaderOnly>
        <SelectMark aria-hidden='true' />
      </StyledIconButton>
    </ToastNotification>
  )
}

export const FavoritesToastNotification: React.FC<{
  toastImage: string
  productHandle: string
  removeToastNotification: () => void
}> = ({ toastImage, productHandle, removeToastNotification }) => {
  const translations = useTranslation()
  return (
    <ToastNotification onAnimationEnd={() => removeToastNotification()}>
      <ToastImage
        src={toastImage}
        srcs={{ sm: toastImage, md: toastImage }}
        aspectRatios={null}
        alt={productHandle}
        widths={{ unit: 'px', sm: 368, md: 368 }}
        loading={'default'}
      ></ToastImage>
      <ToastRight>
        <ToastDescription>
          <TypeStyleHeadlineExtraExtraSmall role='alert' aria-live='polite'>
            {translations.favorites.toastItemAdded}
          </TypeStyleHeadlineExtraExtraSmall>
          <TypeStyleBodySmall>
            <TextLink href='/favorites' as={NextLink}>
              {translations.favorites.toastLink}
            </TextLink>
          </TypeStyleBodySmall>
        </ToastDescription>
      </ToastRight>
      <StyledIconButton
        variant='black-on-white-no-border'
        onClick={() => removeToastNotification()}
      >
        <ScreenReaderOnly>{translations.magnolia.general.closeToast}</ScreenReaderOnly>
        <SelectMark aria-hidden='true' />
      </StyledIconButton>
    </ToastNotification>
  )
}

export const ToastNotifications = () => {
  const {
    currentPromoBarAndNavHeightDesktop,
    currentPromoBarAndNavHeightMobile,
    setPromoBarIsVisible,
    promoBarIsVisible,
  } = NavContainer.useContainer()
  return (
    <>
      <PromoBarIntersection
        setPromoBarIsVisible={setPromoBarIsVisible}
        isVisible={promoBarIsVisible}
      />
      <NextDialogProvider>
        <ToastNotificationsContent
          pushedDownDesktop={currentPromoBarAndNavHeightDesktop}
          pushedDownMobile={currentPromoBarAndNavHeightMobile}
        />
      </NextDialogProvider>
    </>
  )
}

export const RSCToastNotifications = () => {
  const { currentPromoBarAndNavHeightDesktop, currentPromoBarAndNavHeightMobile } = useNavHeader()
  return (
    <NextDialogProvider>
      <ToastNotificationsContent
        pushedDownDesktop={currentPromoBarAndNavHeightDesktop}
        pushedDownMobile={currentPromoBarAndNavHeightMobile}
      />
    </NextDialogProvider>
  )
}

export const ToastNotificationsContent = ({
  pushedDownDesktop,
  pushedDownMobile,
}: {
  pushedDownDesktop: number
  pushedDownMobile: number
}) => {
  const dialogContext = useNextDialogContext()
  const [toastNotification, setToastNotification] = useState<ToastNotificationEvent | null>(null)
  useEffect(() => {
    const handler = (event: CustomEventInit<ToastNotificationEvent>) => {
      const customEventDetail = event.detail ?? null
      setToastNotification(customEventDetail)
      dialogContext?.show()
    }
    window.addEventListener('open-toast-notification', handler)
    return () => window.removeEventListener('open-toast-notification', handler)
  }, [dialogContext, toastNotification])
  const removeToastNotification = () => {
    setToastNotification(null)
  }
  return toastNotification ? (
    <TransparentDialog
      modal={false}
      backdrop={ToastBackdrop}
      hideOnInteractOutside={false}
      autoFocusOnShow={false}
      unmountOnHide={false}
      autoFocusOnHide={false}
    >
      <ToastNotificationWrapper
        key={
          toastNotification.type === 'favorites-add-toast-notification'
            ? toastNotification.payload.productHandle
            : undefined
        }
        $pushedDownPixelsMobile={pushedDownMobile}
        $pushedDownPixelsDesktop={pushedDownDesktop}
      >
        {toastNotification.type === 'favorites-add-toast-notification' ? (
          <FavoritesToastNotification
            productHandle={toastNotification.payload.productHandle}
            toastImage={toastNotification.payload.toastImage}
            removeToastNotification={removeToastNotification}
          />
        ) : toastNotification.type === 'favorites-error-toast-notification' ? (
          <FavoritesErrorToastNotification
            productHandle={toastNotification.payload.productHandle}
            toastImage={toastNotification.payload.toastImage}
            removeToastNotification={removeToastNotification}
          ></FavoritesErrorToastNotification>
        ) : null}
      </ToastNotificationWrapper>
    </TransparentDialog>
  ) : null
}

const TransparentDialog = styled(NextDialog)`
  background-color: transparent;
  max-width: none;
  width: 100%;
`

const ToastImage = styled(ResponsiveImage)`
  height: 88px;
  border-radius: 8px 0 0 8px;
`
const StyledIconButton = styled(IconButton)`
  padding: 0;
  width: 40px;
  height: 40px;
  margin: ${o => o.theme.spacing(2, 2, 0, 0)};
  align-self: flex-start;
`
const ToastDescription = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2)};
`

const ToastRight = styled.div`
  display: flex;
  flex-grow: 1;
`

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`
const ToastNotification = styled.div`
  display: flex;
  box-shadow: 0px 5px 25px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  align-items: center;
  align-self: center;
  gap: ${o => o.theme.spacing(4)};
  background-color: ${o => o.theme.color.fill.background};
  &:hover,
  &:focus-within {
    animation-play-state: paused;
  }
  width: 100%;
  ${fromMd} {
    width: 650px;
  }
  pointer-events: all;
  animation: ${fadeOut} 5s forwards;
`
const ToastNotificationWrapper = styled.div<{
  $pushedDownPixelsDesktop: number
  $pushedDownPixelsMobile: number
}>`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
  background: transparent;
  padding: ${o => o.theme.spacing(0, 4)};
  ${fromMd} {
    margin-top: ${o => o.$pushedDownPixelsDesktop}px;
  }
  margin-top: ${o => o.$pushedDownPixelsMobile}px;
  transition: margin-top ${timingDefault} ease-in-out;
`
const ToastBackdrop = styled.div`
  position: fixed;
  top: ${o => o.theme.spacing(4)};
  ${fromMd} {
    top: ${o => o.theme.spacing(6)};
  }
  right: 50%;
  transform: translate(50%, 0);
  width: 100%;
  transition:
    top,
    right,
    transform ${timingDefault} ease-in-out;
  z-index: ${({ theme }) => theme.zIndex.modals + 1};
  display: flex;
  justify-content: flex-end;
  background-color: transparent;
  pointer-events: none;
`

const PromoBarIntersection = ({
  setPromoBarIsVisible,
  isVisible,
}: {
  setPromoBarIsVisible: (x: boolean) => void
  isVisible: boolean
}) => {
  const promoBarRef = useIntersection(
    ([entry]) => {
      setPromoBarIsVisible(!!entry?.isIntersecting)
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    }
  )
  return <FauxPromoBar ref={promoBarRef} $isVisible={isVisible} />
}
const FauxPromoBar = styled.div<{ $isVisible: boolean }>`
  min-width: 100%;
  min-height: ${promoBarHeightMobile};
  ${fromMd} {
    min-height: ${promoBarHeightDesktop};
  }
  position: absolute;
  top: 0;
  ${o => !o.$isVisible && `min-height: 0; max-height: 0; min-width: 0; max-width: 0;`}
`

const ErrorHeadline = styled(TypeStyleHeadlineExtraExtraSmall)`
  color: ${o => o.theme.color.stroke.critical.text.on.subtle};
  display: flex;
  gap: ${o => o.theme.spacing(2)};
  align-items: center;
`
